"use client";

import { useState } from "react";
import { AlertCircle, Check, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { executeAgentAction } from "@/server-actions/reports/agentActions";

// Exported so sibling components (e.g. CheckInButton's dialog trigger) can match
// this button's look without re-declaring the same class strings.
export const AGENT_ACTION_VARIANTS = {
  primary:
    "bg-blue-600 hover:bg-blue-700 text-white hover:text-white border border-blue-600 hover:border-blue-700",
  secondary:
    "bg-white text-blue-700 border border-blue-200 hover:bg-blue-600 hover:text-white hover:border-blue-600",
  ghost:
    "bg-transparent text-gray-700 border border-gray-200 hover:bg-gray-100 hover:text-gray-900",
};

export const AGENT_ACTION_DONE_CLASS =
  "bg-green-50 hover:bg-green-50 text-green-700 border-green-200 cursor-default";

export const AGENT_ACTION_FAILED_CLASS =
  "bg-red-50 hover:bg-red-100 text-red-700 border-red-200";

export default function AgentActionButton({
  action,
  size = "sm",
  className,
  iconOnly = false,
  onComplete,
  disabled = false,
}) {
  const [state, setState] = useState("idle");
  const [resultMessage, setResultMessage] = useState(null);

  const variantClass =
    AGENT_ACTION_VARIANTS[action.style] || AGENT_ACTION_VARIANTS.secondary;
  const isPending = state === "pending";
  const isDone = state === "done";
  const isFailed = state === "failed";

  const handleClick = async () => {
    if (isPending || isDone) return;
    if (action.confirm && !window.confirm(action.confirm)) return;

    setState("pending");
    setResultMessage(null);

    const result = await executeAgentAction({
      kind: action.kind,
      payload: action.payload,
    });

    if (result?.success) {
      setState("done");
      setResultMessage(result.data?.message || action.successLabel || "Done");
      onComplete?.(result, action);
    } else {
      setState("failed");
      setResultMessage(result?.error || "Action failed. Try again.");
      onComplete?.(result, action);
    }
  };

  const renderIcon = () => {
    if (isPending) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    if (isDone) return <Check className="w-3.5 h-3.5" />;
    if (isFailed) return <AlertCircle className="w-3.5 h-3.5" />;
    return <Send className="w-3.5 h-3.5" />;
  };

  const renderLabel = () => {
    if (iconOnly) return null;
    if (isDone) return action.successLabel || "Sent";
    if (isFailed) return "Retry";
    if (isPending) return action.pendingLabel || "Sending…";
    return action.label;
  };

  const stateClass = isDone
    ? AGENT_ACTION_DONE_CLASS
    : isFailed
      ? AGENT_ACTION_FAILED_CLASS
      : variantClass;

  return (
    <div className={cn("inline-flex flex-col items-start gap-1", className)}>
      <Button
        type="button"
        size={size}
        variant="outline"
        onClick={handleClick}
        disabled={isPending || isDone || disabled}
        className={cn(
          "gap-1.5 font-medium transition-colors",
          stateClass,
          iconOnly && "px-2",
        )}
        title={action.label}
      >
        {renderIcon()}
        {renderLabel()}
      </Button>
      {isFailed && resultMessage ? (
        <span className="text-xs text-red-600 max-w-[280px]">
          {resultMessage}
        </span>
      ) : null}
    </div>
  );
}
